<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading || !isPhoneNumberValid || !isEmailValid"
    loading-message="Creating saved grocery from instacart cart..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Create Saved Grocery From Instacart Cart
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Phone Number*</label>
        <phone-number-input
          id="customer-phone"
          v-model="model.callerPhoneNumber"
          class="col-sm-7"
          :page-id="pageId"
          :required="true"
        />
      </div>
      <div class="row">
        <label class="col-sm-4 control-label pt-10">IC Account Email*</label>
        <div class="col-sm-7">
          <email-input
            v-model="model.instacartEmailAddress"
            required
            @update-validation="updateEmailValidation"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Cart ID*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="cartId"
            v-model="model.cartId"
            placeholder="Cart ID"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Preferred Cart Name</label>
        <div class="col-sm-7">
          <sanitized-input
            id="preferredCartName"
            v-model="model.preferredCartName"
            placeholder="Preferred Cart Name"
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-4 control-label pt-10"> Notes </label>
        <div class="col-sm-7">
          <sanitized-textarea
            :id="`notes-${pageId}`"
            v-model="model.notes"
            autosize
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Create
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, computed, onBeforeUnmount } from 'vue';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { SavedGroceryFromInstacartCartInput } from '@/models/grocery';
import { groceryService } from '@/services/grocery';

const pageId = 'create-saved-grocery-from-instacart-cart';

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const { lastFormData, setFormData } = useGlobal(pageId);

const model: Ref<SavedGroceryFromInstacartCartInput> = ref({
  callerPhoneNumber: lastFormData.value?.callerPhoneNumber ?? '',
  instacartEmailAddress: lastFormData.value?.instacartEmailAddress ?? '',
  cartId: lastFormData.value?.cartId ?? '',
  preferredCartName: lastFormData.value?.preferredCartName ?? '',
  notes: lastFormData.value?.notes ?? '',
});

const isLoading = ref(false);
const isEmailValid = ref(true);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(model.value.callerPhoneNumber));

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const submitForm = async () => {
  isLoading.value = true;

  try {
    const response = await groceryService.createSavedGroceryFromInstacartCart(model.value);
    showSuccessPopupDialog({
      text: response,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  isLoading.value = false;
};

const resetForm = () => {
  model.value = {
    callerPhoneNumber: '',
    instacartEmailAddress: '',
    cartId: '',
    preferredCartName: '',
    notes: '',
  };
};

onBeforeUnmount(() => setFormData(model.value));
</script>
